import { useEffect, useRef, useState } from 'react';
import { toJpeg } from 'html-to-image';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { Copy, Download, FileText, Film, Images, Loader2, Mail, Share2 } from 'lucide-react';
import { Page } from '@generated/components/MediaKit';
import { KIT_PAGES } from '@generated/components/mediaKitPages';
import { RHSR_LOGO, VSR_LOGO } from '@generated/components/AdGuide';

type Vfs = { [file: string]: string };
const pm = pdfMake as typeof pdfMake & { vfs?: Vfs };
const fonts = pdfFonts as unknown as { pdfMake?: { vfs?: Vfs }; vfs?: Vfs };
if (!pm.vfs) { const v = fonts?.pdfMake?.vfs ?? fonts?.vfs; if (v) pm.vfs = v; }

const PORTAL = 'https://ad-portal-gbrook8344s-team-company.v.monday.app';
const W = 1600; // capture width in px; pages are 16:9
const SUBJECT = 'The Star-Revue advertising media kit';
const BODY = `Hello,\n\nHere is the media kit for the Red Hook Star-Revue and The Village Star-Revue. You can flip through it, and plan your campaign, on our ad portal:\n${PORTAL}\n\nI've also attached it as a PDF.\n\nSincerely,\nGeorge Fiala\nPublisher`;

const waitForImages = async (root: HTMLElement) => {
  await new Promise((r) => setTimeout(r, 1200));
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(imgs.map((img) => (img.complete ? Promise.resolve() : new Promise<void>((r) => { img.onload = () => r(); img.onerror = () => r(); }))));
};

/** Staff tool on the Web Ads page: download the whole media kit as a PDF (for email) or one JPG per page (for Facebook / Instagram carousels). */
export function MediaKitShare() {
  const [rendering, setRendering] = useState<null | 'pdf' | 'jpg' | 'video'>(null);
  const [secs, setSecs] = useState(5);
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!rendering) return;
    let cancelled = false;
    (async () => {
      try {
        const host = refs.current[0]?.parentElement;
        if (host) await waitForImages(host);
        const shots: string[] = [];
        for (let k = 0; k < KIT_PAGES.length; k++) {
          const node = refs.current[k]; if (!node || cancelled) continue;
          shots.push(await toJpeg(node, { pixelRatio: 1.35, quality: 0.9, cacheBust: true, backgroundColor: '#ffffff' }));
          setProgress(k + 1);
        }
        if (cancelled) return;
        if (rendering === 'video' && endRef.current) shots.push(await toJpeg(endRef.current, { pixelRatio: 1.35, quality: 0.9, cacheBust: true, backgroundColor: '#ffffff' }));
        if (rendering === 'video') {
          setRecording(true);
          await makeVideo(shots, secs, () => cancelled);
        } else if (rendering === 'pdf') {
          const w = 792; const h = 445.5;
          const content = shots.map((image, i) => ({ image, width: w, height: h, ...(i > 0 ? { pageBreak: 'before' as const } : {}) }));
          pdfMake.createPdf({ pageSize: { width: w, height: h }, pageMargins: [0, 0, 0, 0], content, info: { title: SUBJECT } }).download('Star-Revue-Media-Kit.pdf');
        } else {
          for (let i = 0; i < shots.length; i++) {
            const a = document.createElement('a'); a.href = shots[i] ?? ''; a.download = `Star-Revue-Media-Kit-${String(i + 1).padStart(2, '0')}.jpg`; a.click();
            await new Promise((r) => setTimeout(r, 350));
          }
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('The media kit could not be saved. Please try again.');
      } finally {
        if (!cancelled) { setRendering(null); setProgress(0); setRecording(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [rendering, secs]);

  const start = (k: 'pdf' | 'jpg' | 'video') => { setError(null); setProgress(0); setRendering(k); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(PORTAL); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { console.error(e); setError('Could not copy the link. It is: ' + PORTAL); }
  };
  const btn = 'inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50';

  return (
    <section className="mb-12 border-2 border-foreground bg-card p-5 sm:p-7">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Share the media kit</div>
      <h2 className="mt-1 font-[family-name:var(--font-heading)] text-3xl font-semibold">Media Kit · email &amp; social</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Download the whole {KIT_PAGES.length}-page kit as one PDF to attach to emails, or as one picture per page to post on Facebook or as an Instagram carousel (up to 20 pictures per post). Buttons in the kit don’t work in files, so include the portal link when you share.</p>
      <div className="mt-5 flex flex-wrap gap-2">
        <button type="button" disabled={!!rendering} onClick={() => start('pdf')} className={`${btn} bg-foreground text-background`}>{rendering === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}Download PDF (for email)</button>
        <button type="button" disabled={!!rendering} onClick={() => start('jpg')} className={btn}>{rendering === 'jpg' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Images className="h-4 w-4" />}Download page pictures (Facebook / Instagram)</button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" disabled={!!rendering} onClick={() => start('video')} className={btn}>{rendering === 'video' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}Make a video</button>
        <label className="flex items-center gap-2 text-sm">Seconds per page<select value={secs} disabled={!!rendering} onChange={(e) => setSecs(Number(e.target.value))} className="min-h-11 border border-foreground bg-background px-2 text-sm">{[3, 4, 5, 6, 8, 10, 25].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
        <span className="text-xs text-muted-foreground">About {Math.round((KIT_PAGES.length * secs + Math.max(secs, 6)) / 6) / 10} min long, with a soft fade between pages and a closing card with the portal link. It records in real time, so it takes that long to make.</span>
      </div>
      {rendering && <p className="mt-3 text-sm" role="status">{recording ? 'Recording the video… keep this tab open and in front.' : `Preparing page ${Math.min(progress + 1, KIT_PAGES.length)} of ${KIT_PAGES.length}… keep this tab open.`}</p>}
      {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
      <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-4">
        <span className="mr-1 text-sm font-semibold">Share the live kit:</span>
        <a href={`mailto:?subject=${encodeURIComponent(SUBJECT)}&body=${encodeURIComponent(BODY)}`} className={btn}><Mail className="h-4 w-4" />Start an email</a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(PORTAL)}`} target="_blank" rel="noopener noreferrer" className={btn}><Share2 className="h-4 w-4" />Share on Facebook</a>
        <button type="button" onClick={() => void copy()} className={btn}><Copy className="h-4 w-4" />{copied ? 'Link copied' : 'Copy portal link'}</button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground"><Download className="mr-1 inline h-3 w-3" />Instagram doesn’t allow links in posts. Post the page pictures and put the portal link in your bio.</p>
      {rendering && (
        <div aria-hidden="true" className="pointer-events-none fixed left-[-20000px] top-0">
          {KIT_PAGES.map((pg, k) => (
            <div key={k} ref={(el) => { refs.current[k] = el; }} className="relative aspect-video overflow-hidden bg-card text-foreground [container-type:inline-size]" style={{ width: W }}>
              <Page p={pg} />
            </div>
          ))}
          {rendering === "video" && (
            <div ref={endRef} className="relative flex aspect-video flex-col items-center justify-center bg-[hsl(var(--soft-orange))] text-center text-[hsl(220_30%_6%)] [container-type:inline-size]" style={{ width: W }}>
              <div className="flex items-center gap-[3cqw]"><img src={RHSR_LOGO} alt="" crossOrigin="anonymous" className="h-[11cqw] w-[11cqw]" /><img src={VSR_LOGO} alt="" crossOrigin="anonymous" className="h-[11cqw] w-[11cqw]" /></div>
              <div className="mt-[3cqw] font-[family-name:var(--font-candal)] text-[5cqw] leading-tight text-[hsl(var(--headline-red))]">Plan your campaign</div>
              <div className="mt-[1cqw] text-[1.8cqw] font-semibold"><em>Red Hook Star-Revue</em> &amp; <em>The Village Star-Revue</em></div>
              <div className="mt-[3cqw] bg-[hsl(var(--headline-red))] px-[3cqw] py-[1.4cqw] text-[2.2cqw] font-bold text-background">{PORTAL.replace("https://", "")}</div>
              <div className="mt-[2cqw] text-[1.6cqw] font-semibold">Free ad design · Special deals · Both papers or one</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** Plays the page pictures onto a canvas (with a short crossfade) and records it as a video file. MP4 where the browser supports it, otherwise WebM. */
async function makeVideo(shots: string[], secs: number, isCancelled: () => boolean) {
  const imgs = await Promise.all(shots.map((src) => new Promise<HTMLImageElement>((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = src; })));
  const canvas = document.createElement('canvas'); canvas.width = 1920; canvas.height = 1080;
  const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('No canvas');
  const types = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm'];
  const mimeType = types.find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t));
  if (!mimeType) throw new Error('Video recording not supported');
  const stream = canvas.captureStream(30);
  const rec = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 8_000_000 });
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  const done = new Promise<void>((r) => { rec.onstop = () => r(); });
  const fade = 0.6; const last = Math.max(secs, 6); const total = (imgs.length - 1) * secs + last;
  const draw = (im: HTMLImageElement | undefined, a: number) => { if (!im) return; ctx.globalAlpha = a; ctx.drawImage(im, 0, 0, canvas.width, canvas.height); };
  rec.start(1000);
  const t0 = performance.now();
  await new Promise<void>((resolve) => {
    const tick = () => {
      const t = (performance.now() - t0) / 1000;
      if (t >= total || isCancelled()) { resolve(); return; }
      const k = Math.min(Math.floor(t / secs), imgs.length - 1); const into = t - k * secs;
      ctx.globalAlpha = 1; ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      draw(imgs[k], 1);
      if (into > secs - fade && imgs[k + 1]) draw(imgs[k + 1], (into - (secs - fade)) / fade);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
  rec.stop(); await done; stream.getTracks().forEach((tr) => tr.stop());
  if (isCancelled()) return;
  const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
  const url = URL.createObjectURL(new Blob(chunks, { type: mimeType.split(';')[0] }));
  const a = document.createElement('a'); a.href = url; a.download = `Star-Revue-Media-Kit.${ext}`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
